/**
 * WHEN a finding is allowed to speak — DECISIONS **D-014**.
 *
 * From the design authority, 2026-08-06:
 *
 *   "When I enter the total number of elevators, even after selecting one, a
 *    flag appears: 'Your room surveys account for zero of one elevator. One car
 *    is not accounted for…'. This feels premature and may make users think
 *    they've made a mistake. If an error is to be shown, it should appear at the
 *    end of the setup process, not at the beginning."
 *
 * The rule that falls out of it, and the one this module exists to enforce:
 *
 *   **A cross-entity check may not surface as a fault until the surveyor has
 *   been asked for all of the data it compares.**
 *
 * ── What this does NOT do ────────────────────────────────────────────────────
 * It does not touch `src/core/validation.ts`. V1 still fires, still carries
 * `severity: 'error'`, still reaches `detectGaps`, and still lands on the close
 * screen and in the submitted record exactly as specification §4 rule 1 says it
 * must. Every rule holds at submit time. What changes is the moment the SCREEN
 * is willing to render one of them in red while the surveyor is still being
 * asked the questions it compares.
 *
 * So this is a presentation gate, deliberately kept in `src/ui/state/` where the
 * other reading logic lives, and deliberately keyed by rule id rather than by
 * severity: "is this finding about two things the surveyor may not have both
 * been asked for yet?" is a property of the RULE, not of how bad it is.
 *
 * ── Which rules are held, and why only these ─────────────────────────────────
 * A rule qualifies when its two operands are collected at DIFFERENT TIMES on
 * DIFFERENT SCREENS. V1 compares B11 (typed on the building screen, first thing)
 * against the car lists of every machine room (built later, one screen per
 * room). Between those two moments the comparison is guaranteed to disagree, and
 * saying so is not a finding — it is a report that the job is not finished yet.
 *
 * V7 (car placement) is NOT held here: both of its operands live on the one
 * screen the surveyor is looking at, so the surveyor has already been asked for
 * everything it compares. Its calmer mid-task presentation is a matter for the
 * screen that shows it, not for this gate — see `RoomSetupScreen`'s builder
 * status.
 *
 * ── D-015: the same rule, one screen down ────────────────────────────────────
 * V2 and V3 were first read as immediate for exactly that reason — E1a and E1b
 * are two cards apart on the Electrical screen. But "on the same screen" is not
 * the test. The test is **has the surveyor been asked**, and a question further
 * down a scrolling form has not been asked yet any more than one on a later
 * screen has. §4 rules 2 and 3 demand EXACT equality with the car count, so an
 * Electrical section for a six-car room opens at 0 + 0 = 0 ≠ 6 and turns red
 * before the surveyor has touched anything — the precise defect D-014 was raised
 * about, one screen further in.
 *
 * So a disconnect split is held until BOTH of its questions carry a stored
 * answer. Until then `progressFor()` supplies a calm, `info`-toned sentence
 * about how far the count has got. The moment both have been answered, the
 * engine's own verdict is shown, in red, verbatim — because at that point the
 * surveyor really has claimed something that cannot be true. Untouched, it is
 * still an unanswered required question and still travels to the office as a gap
 * (§4 rule 11); `detectGaps` does not consult this module.
 */

import { answerKey, type Scope, type SurveyDocument, type ValidationIssue } from '../../core/types';
import { isAnswered } from '../../core/validation';
import { isDefaultedKey } from './answerDefaults';
import { crossCheckSettled } from './buildingSetup';

/**
 * Rule ids whose operands are not all in front of the surveyor yet.
 *
 * Adding a rule here is a promise that something else surfaces it at submit —
 * for V1, V2 and V3 that is the gap engine and the close screen, neither of
 * which consults this module.
 */
const HELD_RULES: ReadonlySet<string> = new Set(['V1', 'V2', 'V3', 'V9']);

/**
 * The two questions each disconnect split compares. Both must be answered before
 * the split can be called wrong. Keyed by rule id rather than by question so
 * this module never has to know which section they live on.
 */
const SPLIT_QUESTIONS: Readonly<Record<string, readonly [string, string]>> = {
  V2: ['E1a', 'E1b'],
  V3: ['E5a', 'E5b'],
};

/**
 * Has the SURVEYOR answered this question — **D-016**.
 *
 * Not "does the document hold an answer". Since round 3 every Yes/No question
 * opens already carrying No, so "an answer is present" is true of a section the
 * surveyor has merely looked at, and a gate built on it would let a six-car
 * room's Electrical section open at 0 + 0 ≠ 6 IN RED — the exact defect D-015
 * was raised about, reintroduced by the fix for the one after it.
 *
 * `defaultedAnswers` is the only thing that can tell the two apart (the values
 * are identical), and this is the only place in the app that reads it. A key
 * leaves that list the moment the surveyor writes the answer themselves, so
 * tapping "No" on a question already showing No settles it — which is right:
 * they were asked, they answered.
 */
function answeredIn(doc: SurveyDocument, questionId: string, scope: Scope): boolean {
  const key = answerKey(questionId, scope);
  if (!isAnswered(doc.answers[key])) return false;
  return !isDefaultedKey(doc, key);
}

/**
 * Has the surveyor been asked for everything this rule compares?
 *
 * Delegates where it can: "have all the declared machine rooms had their cars
 * entered" is the building screen's own reading of the document, and there must
 * not be a second copy of it here that can drift.
 */
export function settledFor(doc: SurveyDocument, ruleId: string, scope: Scope = {}): boolean {
  if (ruleId === 'V1') return crossCheckSettled(doc);
  // V9 — the consultant bid's due date (D-029 R4). "Consultant jobs have a bid
  // due date. The office cannot schedule without it" — true, and the rule is
  // untouched: it is a gap and a red line at close. What was wrong was a
  // standing red sentence WHILE the surveyor fills the form, for a date they
  // may simply not have typed yet. A required-field nag speaks once, at close.
  if (ruleId === 'V9') return false;
  const split = SPLIT_QUESTIONS[ruleId];
  if (split) return split.every((questionId) => answeredIn(doc, questionId, scope));
  return true;
}

/** True when this issue must stay quiet for now (D-014, D-015). */
export function heldForNow(doc: SurveyDocument, issue: ValidationIssue): boolean {
  if (!HELD_RULES.has(issue.ruleId)) return false;
  return !settledFor(doc, issue.ruleId, issue.scope);
}

/**
 * The issues a screen may render right now.
 *
 * Everything filtered out here is still in `results`, still a gap at submit, and
 * still the engine's business. This only decides what a surveyor mid-setup is
 * shown in red.
 */
export function shownIssues(
  doc: SurveyDocument,
  issues: readonly ValidationIssue[],
): ValidationIssue[] {
  return issues.filter((issue) => !heldForNow(doc, issue));
}

/*
 * A held finding used to speak anyway — `progressFor` composed "3 of 6 cars
 * of the cars counted so far…" under the split questions. D-029 R4
 * deleted it: running commentary while the surveyor works is noise even when
 * it is calm. A held finding now says NOTHING until it is due.
 */

